/**
 * AM Summary Table
 * One row per Account Manager, each column is a portfolio-wide metric sum.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useAmDashboard } from '../hooks/useAmDashboard';
import type { AmDashboardSummary } from '../types';

interface Am {
  id: string;
  name: string;
  isFormer?: boolean;
}

interface AmSummaryTableProps {
  ams: Am[];
  /** Drill into a single AM's portfolio (sets the dashboard sales-manager scope). */
  onSelectAm: (id: string) => void;
}

const COL_COUNT = 13; // data columns (excluding AM name)

function Cell({
  value,
  highlight,
}: {
  value: number | string;
  highlight?: 'red' | 'amber' | 'blue' | 'orange' | 'green' | 'purple';
}) {
  const colorMap = {
    red: 'text-red-600 font-semibold',
    amber: 'text-amber-600 font-semibold',
    blue: 'text-blue-600 font-semibold',
    orange: 'text-orange-500 font-semibold',
    green: 'text-green-600 font-semibold',
    purple: 'text-purple-600 font-semibold',
  };
  return (
    <span className={cn('tabular-nums', highlight ? colorMap[highlight] : 'text-slate-700')}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </span>
  );
}

function formatRevenue(amount: number) {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
  return `$${amount.toFixed(0)}`;
}

function SummaryRowCells({ s }: { s: AmDashboardSummary }) {
  return (
    <>
      <TableCell className="text-center"><Cell value={s.totalAccounts} /></TableCell>
      <TableCell className="text-center"><Cell value={s.activeTransmitters.count} highlight="green" /></TableCell>
      <TableCell className="text-center">
        <Cell value={s.inactiveTransmitters.count} highlight={s.inactiveTransmitters.count > 0 ? 'red' : undefined} />
      </TableCell>
      <TableCell className="text-center"><Cell value={s.activeSensors?.count ?? 0} highlight="green" /></TableCell>
      <TableCell className="text-center">
        <Cell value={s.inactiveSensors?.count ?? 0} highlight={(s.inactiveSensors?.count ?? 0) > 0 ? 'red' : undefined} />
      </TableCell>
      <TableCell className="text-center">
        <Cell value={s.openTickets?.count ?? 0} highlight={(s.openTickets?.count ?? 0) > 0 ? 'purple' : undefined} />
      </TableCell>
      <TableCell className="text-center">
        <Cell value={s.alertsOpened.count} highlight={s.alertsOpened.count > 0 ? 'amber' : undefined} />
      </TableCell>
      <TableCell className="text-center"><Cell value={s.emailsSent.count} /></TableCell>
      <TableCell className="text-center"><Cell value={s.callsSent.count} /></TableCell>
      <TableCell className="text-center">
        <Cell value={s.expiringContracts.accountsAffected} highlight={s.expiringContracts.accountsAffected > 0 ? 'blue' : undefined} />
      </TableCell>
      <TableCell className="text-center">
        <Cell value={s.lowBatteryDevices.count} highlight={s.lowBatteryDevices.count > 0 ? 'orange' : undefined} />
      </TableCell>
      <TableCell className="text-center">
        <Cell value={s.overdueCalibrations.count} highlight={s.overdueCalibrations.count > 0 ? 'red' : undefined} />
      </TableCell>
      <TableCell className="text-right pr-3">
        <span className="tabular-nums text-slate-700">{formatRevenue(s.revenueLastYear ?? 0)}</span>
      </TableCell>
    </>
  );
}

function SkeletonCells({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <TableCell key={i}>
          <div className="h-3 w-8 rounded bg-slate-200 mx-auto" />
        </TableCell>
      ))}
    </>
  );
}

function TotalsRow() {
  const { data, isLoading, isError } = useAmDashboard({
    pageNumber: 0,
    pageLength: 1,
  });

  const s = data?.summary ?? null;

  if (isLoading) {
    return (
      <TableRow className="animate-pulse bg-slate-100">
        <TableCell className="font-bold text-slate-700">Total</TableCell>
        <SkeletonCells count={COL_COUNT} />
      </TableRow>
    );
  }

  if (isError || !s) return null;

  return (
    <TableRow className="bg-slate-100 border-b-2 border-slate-300">
      <TableCell className="font-bold text-slate-800">Total</TableCell>
      <SummaryRowCells s={s} />
    </TableRow>
  );
}

function UnallocatedRow() {
  const { data, isLoading, isError } = useAmDashboard({
    salesManagerId: 'unallocated',
    pageNumber: 0,
    pageLength: 1,
  });

  const s = data?.summary ?? null;

  if (isLoading) {
    return (
      <TableRow className="animate-pulse">
        <TableCell className="font-medium text-slate-400 italic">Unallocated</TableCell>
        <SkeletonCells count={COL_COUNT} />
      </TableRow>
    );
  }

  if (isError || !s || s.totalAccounts === 0) return null;

  return (
    <TableRow className="border-t-2 border-dashed border-slate-200 bg-slate-50/50">
      <TableCell className="font-medium text-slate-500 italic whitespace-nowrap">Unallocated</TableCell>
      <SummaryRowCells s={s} />
    </TableRow>
  );
}

function AmRow({ am, onSelectAm }: { am: Am; onSelectAm: (id: string) => void }) {
  const { data, isLoading, isError } = useAmDashboard({
    salesManagerId: am.id,
    pageNumber: 0,
    pageLength: 1,
  });

  const s: AmDashboardSummary | null = data?.summary ?? null;

  if (isLoading) {
    return (
      <TableRow className="animate-pulse">
        <TableCell className="font-medium">{am.name}</TableCell>
        <SkeletonCells count={COL_COUNT} />
      </TableRow>
    );
  }

  // Hide former AMs with no accounts — they add noise
  if (am.isFormer && (isError || !s || s.totalAccounts === 0)) return null;

  if (isError || !s) {
    return (
      <TableRow>
        <TableCell className="font-medium">{am.name}</TableCell>
        <TableCell colSpan={COL_COUNT} className="text-xs text-destructive">
          Failed to load
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow
      className={cn('cursor-pointer hover:bg-slate-50', am.isFormer && 'opacity-50')}
      onClick={() => onSelectAm(am.id)}
    >
      <TableCell className={cn('font-medium whitespace-nowrap', am.isFormer && 'italic text-slate-500')}>
        {am.name}
      </TableCell>
      <SummaryRowCells s={s} />
    </TableRow>
  );
}

function Th({ top, bottom, className }: { top: string; bottom: string; className?: string }) {
  return (
    <TableHead className={cn('text-center', className)}>
      <div className="flex flex-col leading-tight">
        <span className="text-[11px] font-semibold">{top}</span>
        <span className="text-[10px] font-normal opacity-75">{bottom}</span>
      </div>
    </TableHead>
  );
}

export function AmSummaryTable({ ams, onSelectAm }: AmSummaryTableProps) {
  if (ams.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No account managers found.
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-white shadow-sm overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="whitespace-nowrap">Account Manager</TableHead>
            <Th top="Total" bottom="Accounts" />
            <Th top="Active" bottom="TX" className="text-green-700" />
            <Th top="Inactive" bottom="TX" className="text-red-600" />
            <Th top="Active" bottom="SSR" className="text-green-700" />
            <Th top="Inactive" bottom="SSR" className="text-red-600" />
            <Th top="Open" bottom="Tickets" className="text-purple-600" />
            <Th top="Alerts" bottom="24h" className="text-amber-600" />
            <Th top="Emails" bottom="24h" />
            <Th top="Calls" bottom="24h" />
            <Th top="Expiring" bottom="Contracts" className="text-blue-600" />
            <Th top="Low" bottom="Battery" className="text-orange-500" />
            <Th top="Overdue" bottom="Cal." className="text-red-600" />
            <Th top="Revenue" bottom="12 months" className="text-emerald-700" />
          </TableRow>
        </TableHeader>
        <TableBody>
          <TotalsRow />
          {ams.map((am) => (
            <AmRow key={am.id} am={am} onSelectAm={onSelectAm} />
          ))}
          <UnallocatedRow />
        </TableBody>
      </Table>
    </div>
  );
}
